import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PaymentGatewayStatus } from '@upweo/types';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  Input,
  useI18n,
} from '@upweo/ui';
import { type FormEvent, useState } from 'react';

import { ApiClientError } from '../lib/api-client.js';
import * as api from '../lib/api.js';

/**
 * Connect a Stripe account.
 *
 * The merchant's own Stripe account, not ours — their customers' money goes
 * straight to them and Upweo is never in the flow of funds.
 *
 * Note that the secret and webhook fields start EMPTY every time, even when an
 * account is already connected. The API will not return those values at all,
 * so there is nothing to pre-fill; the masked key below is the only proof of
 * what is stored. Leaving a field blank means "keep what you have".
 */
export const PaymentsCard = ({ tenantId }: { tenantId: string }): React.JSX.Element => {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery<PaymentGatewayStatus>({
    queryKey: ['payment-gateway', tenantId],
    queryFn: () => api.getPaymentGateway(),
  });

  const [secretKey, setSecretKey] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['payment-gateway'] });
  };

  const connect = useMutation({
    mutationFn: () =>
      api.connectStripe({
        secretKey: secretKey.trim(),
        publishableKey: publishableKey.trim(),
        ...(webhookSecret.trim() ? { webhookSecret: webhookSecret.trim() } : {}),
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      // Clear the inputs immediately: a live secret key sitting in component
      // state is one screenshot or DevTools session away from being leaked.
      setSecretKey('');
      setPublishableKey('');
      setWebhookSecret('');
      setTimeout(() => {
        setSaved(false);
      }, 2500);
      invalidate();
    },
    onError: (err: unknown) => {
      setSaved(false);
      setError(
        err instanceof ApiClientError ? err.error.message : 'Could not save payment settings',
      );
    },
  });

  const disconnect = useMutation({
    mutationFn: () => api.disconnectStripe(),
    onSuccess: invalidate,
    onError: (err: unknown) => {
      setError(err instanceof ApiClientError ? err.error.message : 'Could not disconnect');
    },
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setError(null);
    connect.mutate();
  };

  if (isPending || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('payments.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading…</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle>{t('payments.title')}</CardTitle>
        {data.isEnabled ? (
          <Badge variant="success">{data.isTestMode ? 'Test mode' : 'Live'}</Badge>
        ) : data.isConnected ? (
          <Badge variant="warning">{t('payments.needsWebhook')}</Badge>
        ) : (
          <Badge variant="outline">{t('payments.notConnected')}</Badge>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {!data.isEnabled && (
          <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
            {data.isConnected
              ? 'Your keys are saved, but checkout stays off until a webhook signing secret is added. Without it we can start a payment but never learn that it succeeded — orders would sit unpaid while the money arrives in Stripe.'
              : 'Checkout is disabled until a Stripe account is connected. Shoppers can browse and build a cart, but cannot pay.'}
          </p>
        )}

        {data.isConnected && (
          <dl className="grid gap-2 text-sm sm:grid-cols-[10rem_1fr]">
            <dt className="text-muted-foreground">{t('payments.account')}</dt>
            <dd>{data.accountLabel ?? 'Connected'}</dd>
            <dt className="text-muted-foreground">{t('payments.secretKey')}</dt>
            <dd className="font-mono text-xs">{data.secretKeyMasked ?? '—'}</dd>
            <dt className="text-muted-foreground">{t('payments.publishableKey')}</dt>
            <dd className="font-mono text-xs">{data.publishableKey}</dd>
            <dt className="text-muted-foreground">{t('payments.verified')}</dt>
            <dd>
              {data.lastVerifiedAt ? new Date(data.lastVerifiedAt).toLocaleString() : 'Never'}
            </dd>
          </dl>
        )}

        <div className="space-y-1.5 rounded-md border p-3">
          <p className="text-sm font-medium">{t('payments.webhookEndpoint')}</p>
          <p className="text-xs text-muted-foreground">
            Add this URL in Stripe under Developers → Webhooks, subscribe it to{' '}
            <code className="rounded bg-muted px-1">payment_intent.succeeded</code>,{' '}
            <code className="rounded bg-muted px-1">payment_intent.payment_failed</code>,{' '}
            <code className="rounded bg-muted px-1">payment_intent.canceled</code> and{' '}
            <code className="rounded bg-muted px-1">charge.refunded</code>, then paste the signing
            secret below.
          </p>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded bg-muted px-2 py-1.5 text-xs">
              {data.webhookUrl}
            </code>
            <Button
              type="button"
              variant="outline"
              className="h-8 shrink-0"
              onClick={() => {
                void navigator.clipboard.writeText(data.webhookUrl).then(() => {
                  setCopied(true);
                  setTimeout(() => {
                    setCopied(false);
                  }, 2000);
                });
              }}
            >
              {copied ? t('payments.copied') : t('payments.copy')}
            </Button>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <Field
            label={t('payments.secretKey')}
            htmlFor="p-secret"
            hint={
              data.isConnected
                ? 'Leave blank to keep the key you already saved.'
                : 'Starts with sk_ or rk_. Stored encrypted and never shown again.'
            }
          >
            <Input
              id="p-secret"
              type="password"
              autoComplete="off"
              placeholder={data.isConnected ? '••••••••' : 'sk_test_…'}
              value={secretKey}
              onChange={(e) => {
                setSecretKey(e.target.value);
              }}
            />
          </Field>

          <Field
            label={t('payments.publishableKey')}
            htmlFor="p-publishable"
            hint="Starts with pk_. Sent to the shopper's browser, so this one is public by design."
          >
            <Input
              id="p-publishable"
              autoComplete="off"
              placeholder="pk_test_…"
              value={publishableKey}
              onChange={(e) => {
                setPublishableKey(e.target.value);
              }}
            />
          </Field>

          <Field
            label={t('payments.webhookSecret')}
            htmlFor="p-webhook"
            hint="Starts with whsec_. Shown once in Stripe when you create the endpoint above."
          >
            <Input
              id="p-webhook"
              type="password"
              autoComplete="off"
              placeholder={data.hasWebhookSecret ? '••••••••' : 'whsec_…'}
              value={webhookSecret}
              onChange={(e) => {
                setWebhookSecret(e.target.value);
              }}
            />
          </Field>

          {error && (
            <p
              role="alert"
              className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </p>
          )}

          <div className="flex items-center gap-3">
            <Button
              type="submit"
              disabled={
                connect.isPending || secretKey.trim() === '' || publishableKey.trim() === ''
              }
            >
              {connect.isPending
                ? 'Verifying…'
                : data.isConnected
                  ? 'Update keys'
                  : 'Connect Stripe'}
            </Button>
            {data.isConnected && (
              <Button
                type="button"
                variant="outline"
                disabled={disconnect.isPending}
                onClick={() => {
                  disconnect.mutate();
                }}
              >
                {t('payments.disconnect')}
              </Button>
            )}
            {saved && (
              <span className="text-sm text-muted-foreground">
                {t('payments.savedAndVerified')}
              </span>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
